import _parseInt from '@babel/runtime-corejs3/core-js-stable/parse-int';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.string.split.js';
import _wrapRegExp from '@babel/runtime-corejs3/helpers/esm/wrapRegExp';
import _forEachInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/for-each';
import _Object$keys from '@babel/runtime-corejs3/core-js-stable/object/keys';
import _padStartInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/pad-start';
import 'core-js/modules/es.regexp.to-string.js';

const dateTimeWithOffsetRegEx = /^(19|20)[0-9][0-9]-(0[0-9]|1[0-2])-(0[1-9]|([12][0-9]|3[01]))T([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](\.\d{1,3})?(Z|([-+](0[0-9]|1[0-4])(:?)(00|30|45)))$/; // While not precise enough with possible timezone offsets it does a good enough job for what it is intended

const dateTimeWithoutOffsetRegEx = /^(19|20)[0-9][0-9]-(0?[0-9]|1[0-2])-(0?[1-9]|([12][0-9]|3[01]))(?:T([01]?[0-9]|2[0-3]):[0-5]?[0-9](?::([0-5]?[0-9]))?(?:\.(\d{1,3}))?)?$/;
/**
 * Correctly parses an ISO 8601 date and time string based on how the Bud APIs handle different date formats.
 *
 * @remarks Dates without a timezone offset will be treated as UTC
 * @param datetime - A date or date and time string to be parsed
 * @returns A date object
 * @public
 *
 * @example
 * ```javascript
 * import { parseDate } from '@thisisbud/datetime-utils'
 *
 * const date = parseDate('2011-10-05T14:48:00.000Z')
 * ```
 */

function parseDate(datetime) {
  // Handle fully formed dates with a timezone offset
  if (dateTimeWithOffsetRegEx.test(datetime)) {
    return new Date(datetime); // Handle dates without a timezone offset, assume the date is UTC
  } else if (dateTimeWithoutOffsetRegEx.test(datetime)) {
    const [year, month, day, hours = '0', minutes = '0', seconds = '0', milliseconds = '0'] = datetime.split(/[-T:.]/g);
    return new Date(Date.UTC(_parseInt(year, 10), _parseInt(month, 10) - 1, _parseInt(day, 10), _parseInt(hours, 10), _parseInt(minutes, 10), _parseInt(seconds, 10), _parseInt(milliseconds, 10)));
  }

  throw new Error('Unsupported date format');
}

const durationRegEx = /*#__PURE__*/_wrapRegExp(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/, {
  years: 1,
  months: 2,
  weeks: 3,
  days: 4,
  hours: 6,
  minutes: 7,
  seconds: 8
});
/**
 * Parses an ISO 8601 duration string into the duration of its individual duration units.
 *
 * @param duration - An ISO 8601 duration string to parse
 * @returns The duration for each individual time unit
 * @public
 *
 * @example
 * ```javascript
 * import { parseDuration } from '@thisisbud/datetime-utils'
 *
 * const duration = parseDuration('P23DT3H')
 * // { days: 23, hours: 3 }
 * ```
 */


function parseDuration(duration) {
  var _context;

  if (!durationRegEx.test(duration)) {
    throw new Error('Invalid ISO 8601 duration string');
  }

  const matches = durationRegEx.exec(duration).groups;
  const output = {};

  _forEachInstanceProperty(_context = _Object$keys(matches)).call(_context, function (key) {
    const value = matches[key];

    if (typeof value === 'string') {
      output[key] = _parseInt(value, 10);
    }
  });

  return output;
}

/**
 * Creates a new date with the given duration added to a given date.
 *
 * @param date - The date to add a duration to
 * @param duration - The duration to add to the given date either as an ISO 8601 string or a duration descriptor object
 * @returns A new date with the given duration added to the given date
 * @public
 *
 * @example
 * ```javascript
 * import { addDuration } from '@thisisbud/datetime-utils'
 *
 * const output = addDuration(new Date(2021, 5, 14, 0, 0, 0, 0), { months: 1 })
 * // 2021-07-14T00:00:00.000Z
 * ```
 */

function addDuration(date, duration) {
  const normalizedDuration = typeof duration === 'string' ? parseDuration(duration) : duration;
  const output = new Date(date.getTime());

  if (typeof normalizedDuration.years === 'number') {
    output.setFullYear(output.getFullYear() + normalizedDuration.years);
  }

  if (typeof normalizedDuration.months === 'number') {
    output.setMonth(output.getMonth() + normalizedDuration.months);
  }

  if (typeof normalizedDuration.weeks === 'number') {
    output.setDate(output.getDate() + normalizedDuration.weeks * 7); // eslint-disable-line @typescript-eslint/no-extra-parens
  }

  if (typeof normalizedDuration.days === 'number') {
    output.setDate(output.getDate() + normalizedDuration.days);
  }

  if (typeof normalizedDuration.hours === 'number') {
    output.setHours(output.getHours() + normalizedDuration.hours);
  }

  if (typeof normalizedDuration.minutes === 'number') {
    output.setMinutes(output.getMinutes() + normalizedDuration.minutes);
  }

  if (typeof normalizedDuration.seconds === 'number') {
    output.setSeconds(output.getSeconds() + normalizedDuration.seconds);
  }

  if (typeof normalizedDuration.milliseconds === 'number') {
    output.setMilliseconds(output.getMilliseconds() + normalizedDuration.milliseconds);
  }

  return output;
}

/**
 * Creates a new date with the given duration subtracted from a given date.
 *
 * @param date - The date to subtract a duration from
 * @param duration - The duration to subtract from the given date either as an ISO 8601 string or a duration descriptor object
 * @returns A new date with the given duration subtracted from the given date
 * @public
 *
 * @example
 * ```javascript
 * import { subtractDuration } from '@thisisbud/datetime-utils'
 *
 * const output = subtractDuration(new Date(2021, 5, 14, 0, 0, 0, 0), { months: 1 })
 * // 2021-05-14T00:00:00.000Z
 * ```
 */

function subtractDuration(date, duration) {
  const normalizedDuration = typeof duration === 'string' ? parseDuration(duration) : duration;
  const output = new Date(date.getTime());

  if (typeof normalizedDuration.years === 'number') {
    output.setFullYear(output.getFullYear() - normalizedDuration.years);
  }

  if (typeof normalizedDuration.months === 'number') {
    output.setMonth(output.getMonth() - normalizedDuration.months);
  }

  if (typeof normalizedDuration.weeks === 'number') {
    output.setDate(output.getDate() - normalizedDuration.weeks * 7); // eslint-disable-line @typescript-eslint/no-extra-parens
  }

  if (typeof normalizedDuration.days === 'number') {
    output.setDate(output.getDate() - normalizedDuration.days);
  }

  if (typeof normalizedDuration.hours === 'number') {
    output.setHours(output.getHours() - normalizedDuration.hours);
  }

  if (typeof normalizedDuration.minutes === 'number') {
    output.setMinutes(output.getMinutes() - normalizedDuration.minutes);
  }

  if (typeof normalizedDuration.seconds === 'number') {
    output.setSeconds(output.getSeconds() - normalizedDuration.seconds);
  }

  if (typeof normalizedDuration.milliseconds === 'number') {
    output.setMilliseconds(output.getMilliseconds() - normalizedDuration.milliseconds);
  }

  return output;
}

/**
 * Converts an ISO 8601 duration string or a duration descriptor object to its equivalent in days.
 *
 * @param duration - The duration to convert either as an ISO 8601 string or a duration descriptor object
 * @returns The duration as a number of days
 * @public
 *
 * @example
 * ```javascript
 * import { durationAsDays } from '@thisisbud/datetime-utils'
 *
 * const output = durationAsDays('P3M')
 * // 90
 * ```
 */

function durationAsDays(duration) {
  const normalizedDuration = typeof duration === 'string' ? parseDuration(duration) : duration;
  let output = 0;

  if (typeof normalizedDuration.years === 'number') {
    output += normalizedDuration.years * 365;
  }

  if (typeof normalizedDuration.months === 'number') {
    output += normalizedDuration.months * 30;
  }

  if (typeof normalizedDuration.weeks === 'number') {
    output += normalizedDuration.weeks * 7;
  }

  if (typeof normalizedDuration.days === 'number') {
    output += normalizedDuration.days;
  }

  if (typeof normalizedDuration.hours === 'number') {
    output += normalizedDuration.hours / 24;
  }

  if (typeof normalizedDuration.minutes === 'number') {
    output += normalizedDuration.minutes / 1440;
  }

  if (typeof normalizedDuration.seconds === 'number') {
    output += normalizedDuration.seconds / 86400;
  }

  if (typeof normalizedDuration.milliseconds === 'number') {
    output += normalizedDuration.milliseconds / 86400000;
  }

  return output;
}

const daysInWeek = 7;
/**
 * Converts an ISO 8601 duration string or a duration descriptor object to its equivalent in weeks.
 *
 * @param duration - The duration to convert either as an ISO 8601 string or a duration descriptor object
 * @returns The duration as a number of weeks
 * @public
 *
 * @example
 * ```javascript
 * import { durationAsWeeks } from '@thisisbud/datetime-utils'
 *
 * const output = durationAsWeeks('P13W')
 * // 13
 * ```
 */

function durationAsWeeks(duration) {
  const durationInDays = durationAsDays(duration);
  return durationInDays / daysInWeek;
}

const daysInMonth = 30;
/**
 * Converts an ISO 8601 duration string or a duration descriptor object to its equivalent in months.
 *
 * @param duration - The duration to convert either as an ISO 8601 string or a duration descriptor object
 * @returns The duration as a number of months
 * @public
 *
 * @example
 * ```javascript
 * import { durationAsMonths } from '@thisisbud/datetime-utils'
 *
 * const output = durationAsMonths('P90D')
 * // 3
 * ```
 */

function durationAsMonths(duration) {
  const durationInDays = durationAsDays(duration);
  return durationInDays / daysInMonth;
}

/**
 * Get a date range with a specified `from` or `to` date and the given duration.
 *
 * @param pivotDate - An object containing a `from` or a `to` date to act as a starting or end point of the range
 * @param duration - The duration of the date range, which will be subtracted or added to `pivotDate` depending on the required direction
 * @returns An object containing `from` and `to` dates
 * @public
 *
 * @example
 * ```javascript
 * import { getDateRangeWithDuration } from '@thisisbud/datetime-utils'
 *
 * const output = getDateRangeWithDuration({ from: new Date(2021, 5, 14, 0, 0, 0, 0) }, { months: 1 })
 * // { from: 2021-06-14T00:00:00.000Z, to: 2021-07-14T00:00:00.000Z }
 * ```
 */

function getDateRangeWithDuration(pivotDate, duration) {
  // @ts-expect-error Depending on the target direction either one or the other will be provided
  const {
    from,
    to
  } = pivotDate;

  if (typeof from !== 'undefined') {
    return {
      from: from,
      to: addDuration(from, duration)
    };
  } else if (typeof to !== 'undefined') {
    return {
      from: subtractDuration(to, duration),
      to: to
    };
  }

  throw new Error('A from or to pivot date must be provided');
}

/**
 * Create an ISO 8601 local string representation of a date object.
 *
 * @remarks The output will vary depending on the device's timezone
 * @param date - A date object
 * @returns An ISO 8601 local date string representation of the date provided
 * @public
 *
 * @example
 * ```javascript
 * import { toLocalDateString } from '@thisisbud/datetime-utils'
 *
 * const output = toUTCDateString(new Date(1987, 4, 14))
 * // 1987-05-14
 * ```
 */
function toLocalDateString(date) {
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString();
  const day = date.getDate().toString();
  return `${_padStartInstanceProperty(year).call(year, 4, '0')}-${_padStartInstanceProperty(month).call(month, 2, '0')}-${_padStartInstanceProperty(day).call(day, 2, '0')}`;
}

/**
 * Create an ISO 8601 UTC string representation of a date object.
 *
 * @param date - A date object
 * @returns An ISO 8601 UTC date string representation of the date provided
 * @public
 *
 * @example
 * ```javascript
 * import { toUTCDateString } from '@thisisbud/datetime-utils'
 *
 * const output = toUTCDateString(new Date(Date.UTC(1987, 4, 14)))
 * // 1987-05-14
 * ```
 */
function toUTCDateString(date) {
  const year = date.getUTCFullYear().toString();
  const month = (date.getUTCMonth() + 1).toString();
  const day = date.getUTCDate().toString();
  return `${_padStartInstanceProperty(year).call(year, 4, '0')}-${_padStartInstanceProperty(month).call(month, 2, '0')}-${_padStartInstanceProperty(day).call(day, 2, '0')}`;
}

/**
 * Transposes a local date into a UTC date, keeping the same year, month, day, hour, minute, seconds and millisecond values.
 *
 * @remarks The resulting date timestamp will vary depending on the device's timezone
 * @param date - A local date object
 * @returns A UTC date object with the same year, month, day etc as the given local date
 * @public
 *
 * @example
 * ```javascript
 * import { localDateToUTCDate } from '@thisisbud/datetime-utils'
 *
 * const output = localDateToUTCDate(new Date(1987, 4, 14, 0, 0, 0, 0))
 * // 1987-05-14T00:00:00.000Z
 * ```
 */
function localDateToUTCDate(date) {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
}

/**
 * Transposes a UTC date into a local date, keeping the same year, month, day, hour, minute, seconds and millisecond values.
 *
 * @remarks The resulting date timestamp will vary depending on the device's timezone
 * @param date - A UTC date object
 * @returns A local date object with the same year, month, day etc as the given UTC date
 * @public
 *
 * @example
 * ```javascript
 * import { utcDateToLocalDate } from '@thisisbud/datetime-utils'
 *
 * const output = utcDateToLocalDate(new Date(Date.UTC(1987, 4, 14, 0, 0, 0, 0)))
 * // 1987-05-14T00:00:00.000+01:00
 * ```
 */
function utcDateToLocalDate(date) {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
}

export { addDuration, durationAsDays, durationAsMonths, durationAsWeeks, getDateRangeWithDuration, localDateToUTCDate, parseDate, parseDuration, subtractDuration, toLocalDateString, toUTCDateString, utcDateToLocalDate };
